import React from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Nav, ProgressBar, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import IconButton from '../IconButton';
import { useOrderWizardContext } from './OrderWizardProvider';

// Importar los componentes de cada paso
import BasicInformationStep from './steps/BasicInformationStep';
import FinancialInformationStep from './steps/FinancialInformationStep';
import AdvancedConfigurationStep from './steps/AdvancedConfigurationStep';
import FinalSummaryStep from './steps/FinalSummaryStep';

const OrderWizardLayout = ({ 
  show, 
  onHide, 
  onSubmit, 
  saving = false,
  variant = 'default',
  progressBar = true,
  clients = [],
  businessUnits = [],
  loadingClients = false,
  availableAccounts = [],
  loadingAccounts = false,
  currencies = ['USD', 'EUR', 'COP'],
  editingOrder = null,
  onClientChange,
  onOpenAdminFeeModal
}) => {
  const {
    currentStep,
    completedSteps,
    validationErrors,
    orderData,
    isEditMode,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    resetWizard,
    loadOrderData,

  } = useOrderWizardContext();

  // Configuración de los pasos del wizard
  const wizardSteps = [
    {
      id: 1,
      icon: 'info-circle',
      label: 'Información Básica',
      description: 'Cliente y detalles generales'
    },
    {
      id: 2,
      icon: 'dollar-sign',
      label: 'Información Financiera',
      description: 'Fechas, moneda e inversión'
    },
    {
      id: 3,
      icon: 'cogs',
      label: 'Configuración Avanzada',
      description: 'Cuenta y configuración final'
    },
    {
      id: 4,
      icon: 'check-circle',
      label: 'Resumen Final',
      description: 'Revisa y confirma la orden'
    }
  ];

  // Manejar cierre del modal
  const handleClose = () => {
    resetWizard();
    onHide();
  };

  // Manejar navegación entre pasos
  const handleStepNavigation = (targetStep) => {
    if (targetStep < currentStep || completedSteps.includes(targetStep - 1)) {
      goToStep(targetStep);
    } else if (targetStep === currentStep + 1) {
      goToNextStep();
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === 4) {
      await onSubmit(orderData);
    } else {
      goToNextStep();
    }
  };

  // Efecto para cargar datos en modo edición
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (editingOrder && show) {
     
      
      // Si no hay clienteId en editingOrder, intentar encontrarlo por nombre
      if (editingOrder && (!editingOrder.clienteId || editingOrder.clienteId === '') && clients.length > 0) {
        // Buscar por clienteName o cliente
        const clientNameToSearch = editingOrder.clienteName || editingOrder.clientName || editingOrder.cliente || '';
        
        const foundClient = clients.find(c => 
          c.ClientName === clientNameToSearch ||
          c.ClientName.toLowerCase().includes(clientNameToSearch.toLowerCase()) ||
          clientNameToSearch.toLowerCase().includes(c.ClientName.toLowerCase())
        );
        
        if (foundClient) {
          // Crear una copia de editingOrder con el clienteId correcto
          const orderWithClientId = {
            ...editingOrder,
            clienteId: foundClient.ClientID,
            clienteName: foundClient.ClientName
          };
          loadOrderData(orderWithClientId);
          
          // Cargar las cuentas automáticamente
          if (onClientChange) {
            onClientChange(foundClient.ClientID);
          }
        } else {
          loadOrderData(editingOrder);
        }
      } else {
        loadOrderData(editingOrder);
      }
    } else if (show && !editingOrder) {
      resetWizard();
    }
  }, [editingOrder, show, clients]);

  // Componente para renderizar cada paso de navegación
  const NavItem = ({ step, isActive, isCompleted, isAccessible, onClick }) => {
    const stepConfig = wizardSteps.find(s => s.id === step);
    
    return (
      <Nav.Item>
        <Nav.Link
          className={classNames('fw-semibold px-3 py-2', {
            'done': isCompleted,
            'active': isActive,
            'text-muted': !isAccessible && !isActive && !isCompleted
          })}
          onClick={() => isAccessible && onClick(step)}
          style={{ cursor: isAccessible ? 'pointer' : 'not-allowed' }}
        >
          <div className="d-flex align-items-center">
            <span className="nav-item-circle-parent me-2">
              <span className={classNames('nav-item-circle d-flex align-items-center justify-content-center', {
                'bg-success text-white border-success': isCompleted,
                'bg-primary text-white border-primary': isActive,
                'bg-light text-muted border-light': !isActive && !isCompleted
              })}>
                {isCompleted ? (
                  <FontAwesomeIcon icon="check" className="fs-10" />
                ) : (
                  <FontAwesomeIcon icon={stepConfig?.icon} className="fs-10" />
                )}
              </span>
            </span>
            <div className="d-none d-md-block">
              <div className="fs-10 fw-bold">{stepConfig?.label}</div>
              <div className="fs-11 text-muted">{stepConfig?.description}</div>
            </div>
          </div>
        </Nav.Link>
      </Nav.Item>
    );
  };

  // Renderizar el contenido del paso actual
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInformationStep
            clients={clients}
            businessUnits={businessUnits}
            loadingClients={loadingClients}
            validationErrors={validationErrors}
            onClientChange={onClientChange}
          />
        );
      case 2:
        return (
          <FinancialInformationStep
            currencies={currencies}
            validationErrors={validationErrors}
            onOpenAdminFeeModal={onOpenAdminFeeModal}
          />
        );
      case 3:
        return (
          <AdvancedConfigurationStep
            availableAccounts={availableAccounts}
            loadingAccounts={loadingAccounts}
          />
        );
      case 4:
        return (
          <FinalSummaryStep
            availableAccounts={availableAccounts}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon="plus-circle" className="me-2 text-primary" />
          {isEditMode ? 'Editar Orden de Inserción' : 'Nueva Orden de Inserción'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit} noValidate>
        <Card className="border-0 order-wizard">
          <Card.Header className={classNames('bg-body-tertiary border-bottom', {
            'px-4 py-3': variant === 'pills',
            'pb-2': variant !== 'pills'
          })}>
            <Nav className="justify-content-center" variant={variant}>
              {wizardSteps.map((step) => {
                const isActive = currentStep === step.id;
                const isCompleted = completedSteps.includes(step.id);
                const isAccessible = step.id <= currentStep || isCompleted;
                
                return (
                  <NavItem
                    key={step.id}
                    step={step.id}
                    isActive={isActive}
                    isCompleted={isCompleted}
                    isAccessible={isAccessible}
                    onClick={handleStepNavigation}
                  />
                );
              })}
            </Nav>
          </Card.Header>

          {progressBar && (
            <ProgressBar 
              now={(currentStep / wizardSteps.length) * 100} 
              style={{ height: 3 }}
              className="rounded-0"
            />
          )}

          <Card.Body className="fw-normal px-4 py-4">
            {/* Mostrar errores de validación si los hay */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="alert alert-danger mb-4">
                <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                <strong>Por favor corrige los siguientes errores:</strong>
                <ul className="mb-0 mt-2">
                  {Object.values(validationErrors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contenido del paso actual */}
            {renderStepContent()}
          </Card.Body>

          <Card.Footer className={classNames('px-4 bg-body-tertiary border-top d-flex justify-content-between align-items-center')}>
            <div>
              {currentStep > 1 && (
                <IconButton
                  variant="outline-secondary"
                  icon="chevron-left"
                  iconAlign="left"
                  onClick={goToPreviousStep}
                  disabled={saving}
                >
                  Anterior
                </IconButton>
              )}
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                disabled={saving}
              >
                <FontAwesomeIcon icon="times" className="me-1" />
                Cancelar
              </Button>

              <IconButton
                variant="primary"
                type="submit"
                icon={currentStep === 4 ? "check" : "chevron-right"}
                iconAlign="right"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Guardando...</span>
                    </div>
                    Guardando...
                  </>
                ) : currentStep === 4 ? (
                  isEditMode ? 'Actualizar Orden' : 'Crear Orden'
                ) : (
                  'Siguiente'
                )}
              </IconButton>
            </div>
          </Card.Footer>
        </Card>
      </Form>
    </Modal>
  );
};

OrderWizardLayout.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  variant: PropTypes.oneOf(['pills', 'default']),
  progressBar: PropTypes.bool,
  clients: PropTypes.array,
  businessUnits: PropTypes.array,
  loadingClients: PropTypes.bool,
  availableAccounts: PropTypes.array,
  loadingAccounts: PropTypes.bool,
  currencies: PropTypes.array,
  editingOrder: PropTypes.object,
  onClientChange: PropTypes.func,
  onOpenAdminFeeModal: PropTypes.func
};

export default OrderWizardLayout; 